"use client";

import {
  createContext,
  memo,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { ChevronDownIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { useAuiState, useScrollLock } from "@assistant-ui/react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const ANIMATION_DURATION = 200;

const toolGroupVariants = cva("aui-tool-group-root group/tool-group my-2 w-full", {
  variants: {
    variant: {
      outline: "rounded-xl border py-1",
      ghost: "",
      muted: "bg-muted/40 rounded-xl px-1 py-1",
    },
  },
  defaultVariants: { variant: "outline" },
});

export type ToolGroupRootProps = Omit<
  React.ComponentProps<typeof Collapsible>,
  "open" | "onOpenChange"
> &
  VariantProps<typeof toolGroupVariants> & {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    defaultOpen?: boolean;
  };

function ToolGroupRoot({
  className,
  variant,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen = false,
  children,
  ...props
}: ToolGroupRootProps) {
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const lockScroll = useScrollLock(collapsibleRef, ANIMATION_DURATION);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      lockScroll();
      if (!isControlled) {
        setUncontrolledOpen(open);
      }
      controlledOnOpenChange?.(open);
    },
    [lockScroll, isControlled, controlledOnOpenChange],
  );

  return (
    <Collapsible
      ref={collapsibleRef}
      data-slot="tool-group-root"
      data-variant={variant ?? "outline"}
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn(
        toolGroupVariants({ variant }),
        "group/tool-group-root",
        className,
      )}
      style={
        {
          "--animation-duration": `${ANIMATION_DURATION}ms`,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </Collapsible>
  );
}

const toPersianDigits = (n: number) => n.toLocaleString("fa-IR");

/** Same dots as the thinking indicator (`.mcp-thinking__dots`). */
function ToolGroupRunningDots() {
  return (
    <span
      data-slot="tool-group-trigger-dots"
      className="mcp-thinking__dots shrink-0"
      aria-hidden
    >
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * Name of the tool currently running in a group (the latest step), falling
 * back to the last tool call once nothing is running.
 */
function useToolGroupCurrentToolName(indices: readonly number[]) {
  return useAuiState((s) => {
    let last: string | undefined;
    for (let i = indices.length - 1; i >= 0; i--) {
      const part = s.message.parts[indices[i]!];
      if (part?.type !== "tool-call") continue;
      if (part.result === undefined) return part.toolName;
      last ??= part.toolName;
    }
    return last;
  });
}

export type ToolGroupLiveState = "tool" | "thinking" | null;

/**
 * Live state of a group while its message runs and it is the last part:
 * `"tool"` while a tool still awaits its result, `"thinking"` once all tools
 * returned and the model is working on the next step; otherwise `null`.
 */
function useToolGroupLiveState(indices: readonly number[]): ToolGroupLiveState {
  return useAuiState((s) => {
    if (s.message.status?.type !== "running") return null;
    const lastIndex = indices[indices.length - 1];
    if (lastIndex !== s.message.parts.length - 1) return null;
    const toolRunning = indices.some((i) => {
      const part = s.message.parts[i];
      return part?.type === "tool-call" && part.result === undefined;
    });
    return toolRunning ? "tool" : "thinking";
  });
}

/**
 * Lets a tool group that shows the live state (running tool / thinking) tell
 * the message so the separate thinking indicator can step aside.
 */
const ToolGroupThinkingContext = createContext<
  ((thinking: boolean) => void) | null
>(null);

function ToolGroupTrigger({
  count,
  active = false,
  currentTool,
  thinkingLabel,
  className,
  ...props
}: React.ComponentProps<typeof CollapsibleTrigger> & {
  count: number;
  active?: boolean;
  /** Display name of the running step; shown next to the count while active. */
  currentTool?: string;
  /** When set, replaces the count label (model is thinking after the tools). */
  thinkingLabel?: string;
}) {
  const thinking = !active && !!thinkingLabel;
  const label = thinking
    ? thinkingLabel
    : `استفاده از ${toPersianDigits(count)} ابزار`;
  const showCurrent = active && !!currentTool;

  return (
    <CollapsibleTrigger
      data-slot="tool-group-trigger"
      dir="rtl"
      className={cn(
        "aui-tool-group-trigger group/trigger",
        // Kill Bootstrap/browser button chrome on Radix trigger.
        "!m-0 !inline-flex !h-auto !w-auto !max-w-full !cursor-pointer !items-center !gap-2 !rounded-md !border-0 !bg-transparent !px-0 !py-1 !text-sm !shadow-none !outline-none",
        "text-muted-foreground hover:text-foreground transition-colors",
        "focus-visible:!ring-ring focus-visible:!ring-2 focus-visible:!ring-offset-0",
        className,
      )}
      {...props}
    >
      <span
        data-slot="tool-group-trigger-label"
        className="aui-tool-group-trigger-label-wrapper min-w-0 truncate text-start text-[0.8rem] leading-none"
      >
        {thinking ? (
          <span className="mcp-thinking__label">{label}</span>
        ) : (
          label
        )}
        {showCurrent && (
          <>
            <span className="mx-1.5 opacity-50" aria-hidden>
              ·
            </span>
            <span
              key={currentTool}
              data-slot="tool-group-trigger-current"
              className="text-foreground/80 animate-in fade-in-0 duration-200"
            >
              {currentTool}
            </span>
          </>
        )}
      </span>
      {(active || thinking) && <ToolGroupRunningDots />}
      <ChevronDownIcon
        data-slot="tool-group-trigger-chevron"
        className={cn(
          "aui-tool-group-trigger-chevron size-3.5 shrink-0 opacity-60",
          "transition-transform duration-(--animation-duration) ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none",
          // Closed: tip points left (←); open: tip points down (↓).
          "rotate-90",
          "group-data-open/trigger:rotate-0",
          "group-data-panel-open/trigger:rotate-0",
        )}
      />
    </CollapsibleTrigger>
  );
}

function ToolGroupContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      data-slot="tool-group-content"
      className={cn(
        "aui-tool-group-content relative overflow-hidden text-sm outline-none",
        "group/collapsible-content ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:animate-none",
        "data-closed:animate-collapsible-up",
        "data-open:animate-collapsible-down",
        "data-closed:fill-mode-forwards",
        "data-closed:pointer-events-none",
        "[--tw-duration:var(--animation-duration)]",
        className,
      )}
      {...props}
    >
      <div
        data-slot="tool-group-list"
        className={cn(
          "mt-0.5 flex flex-col",
          // Rows inside the list drop their standalone card chrome.
          "[&>[data-slot=tool-fallback-root]]:rounded-none [&>[data-slot=tool-fallback-root]]:border-0",
          "[&_[data-slot=tool-fallback-trigger]]:!px-0 [&_[data-slot=tool-fallback-trigger]]:!py-1.5",
          "[&>*]:animate-in [&>*]:fade-in-0 [&>*]:animation-duration-(--animation-duration) [&>*]:ease-[cubic-bezier(0.32,0.72,0,1)]",
          "[&>*]:motion-reduce:animate-none",
        )}
      >
        {children}
      </div>
    </CollapsibleContent>
  );
}

/**
 * Minimal tool group: open while it is the live step (tools running or the
 * model thinking after them), collapses afterwards — unless the user toggled
 * it manually.
 */
function ToolGroupBlock({
  count,
  active = false,
  currentTool,
  thinkingLabel,
  children,
}: PropsWithChildren<{
  count: number;
  active?: boolean;
  currentTool?: string;
  thinkingLabel?: string;
}>) {
  const reportLive = useContext(ToolGroupThinkingContext);
  const live = active || !!thinkingLabel;
  useLayoutEffect(() => {
    if (!live || !reportLive) return;
    reportLive(true);
    return () => reportLive(false);
  }, [live, reportLive]);

  const [open, setOpen] = useState(live);
  const [userToggled, setUserToggled] = useState(false);
  const [prevLive, setPrevLive] = useState(live);
  if (live !== prevLive) {
    setPrevLive(live);
    if (!userToggled) setOpen(live);
  }

  return (
    <ToolGroupRoot
      variant="ghost"
      open={open}
      onOpenChange={(next) => {
        setUserToggled(true);
        setOpen(next);
      }}
    >
      <ToolGroupTrigger
        count={count}
        active={active}
        currentTool={currentTool}
        thinkingLabel={thinkingLabel}
      />
      <ToolGroupContent>{children}</ToolGroupContent>
    </ToolGroupRoot>
  );
}

type ToolGroupComponent = FC<
  PropsWithChildren<{ startIndex: number; endIndex: number }>
> & {
  Root: typeof ToolGroupRoot;
  Trigger: typeof ToolGroupTrigger;
  Content: typeof ToolGroupContent;
};

const ToolGroupImpl: FC<
  PropsWithChildren<{ startIndex: number; endIndex: number }>
> = ({ children, startIndex, endIndex }) => {
  const toolCount = endIndex - startIndex + 1;

  return <ToolGroupBlock count={toolCount}>{children}</ToolGroupBlock>;
};

/**
 * @deprecated This wrapper targets the legacy `components.ToolGroup` prop
 * on `<MessagePrimitive.Parts>`. Use `<MessagePrimitive.GroupedParts>` with
 * a `groupBy` returning `"group-tool"` and compose `ToolGroupRoot` /
 * `ToolGroupTrigger` / `ToolGroupContent` directly. See `thread.tsx`.
 */
const ToolGroup = memo(ToolGroupImpl) as unknown as ToolGroupComponent;

ToolGroup.displayName = "ToolGroup";
ToolGroup.Root = ToolGroupRoot;
ToolGroup.Trigger = ToolGroupTrigger;
ToolGroup.Content = ToolGroupContent;

export {
  ToolGroup,
  ToolGroupBlock,
  ToolGroupRoot,
  ToolGroupThinkingContext,
  useToolGroupCurrentToolName,
  useToolGroupLiveState,
  ToolGroupTrigger,
  ToolGroupContent,
  toolGroupVariants,
};
